import { useCallback, useMemo, useState } from "react";
import { FolderOpen, Plus, Search, Trash2 } from "lucide-react";
import { AddProject, type ProjectCreationMode } from "../../components/AddProject.tsx";
import { ConfirmDialog, Dialog } from "../../components/Dialog.tsx";
import { navigate, projectPath } from "../../lib/router.ts";
import { useMachines } from "../../lib/useMachines.ts";
import { useProjectAdmin } from "../../lib/useProjectAdmin.ts";

/**
 * Projects management:
 * Clear header with Search and "+ New Project", clean Project rows with useful context
 * (Project name, Workspace, Machine, local path, availability), and a unified modal for
 * Create, Clone Repository, and Add Existing. Standalone Approved Workspaces card is removed.
 */
export function ProjectsSection() {
  const { machines } = useMachines();
  const search = typeof location !== "undefined" ? location.search : "";
  const preferred = useMemo(() => new URLSearchParams(search).get("add"), [search]);
  const urlMode = useMemo<ProjectCreationMode | null>(() => {
    const m = new URLSearchParams(search).get("mode");
    if (m === "add" || m === "clone" || m === "create") return m;
    return null;
  }, [search]);

  const online = machines?.filter((m) => m.online) ?? [];
  const [picked, setPicked] = useState<string | null>(preferred);
  const machineId = picked && machines?.some((m) => m.id === picked)
    ? picked
    : (online[0]?.id ?? machines?.[0]?.id ?? null);

  const currentMachine = machines?.find((m) => m.id === machineId) ?? null;
  const isMachineOnline = currentMachine?.online ?? false;

  const admin = useProjectAdmin(isMachineOnline ? machineId : null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [newProjectOpen, setNewProjectOpen] = useState(Boolean(preferred || urlMode));

  const workspaceName = useCallback(
    (id: string): string => {
      const ws = admin.workspaces?.find((w) => w.id === id);
      return ws ? ws.name : "";
    },
    [admin.workspaces],
  );

  const filteredProjects = useMemo(() => {
    if (!admin.projects) return [];
    if (!projectSearch.trim()) return admin.projects;
    const q = projectSearch.toLowerCase();
    return admin.projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.location.toLowerCase().includes(q) ||
        workspaceName(p.workspaceId).toLowerCase().includes(q),
    );
  }, [admin.projects, projectSearch, workspaceName]);

  return (
    <div className="projects-management" data-testid="projects-management">
      {machines === null && <p className="muted small">Loading machines…</p>}

      {machines && machines.length === 0 && (
        <p className="muted small">
          No machine is paired yet. Start and pair a machine with <code>racler agent run</code>.
        </p>
      )}

      {machines && machines.length > 0 && (
        <>
          {/* Header Section */}
          <section className="settings-card set-block projects-header-section">
            <div className="projects-header-bar">
              <div className="projects-header-left">
                <h3 className="settings-card-title">Projects</h3>
                <p className="settings-card-desc">
                  Manage registered projects, repositories, and local folders.
                </p>
              </div>

              <div className="projects-header-actions">
                {machines.length > 1 && (
                  <select
                    value={machineId ?? ""}
                    onChange={(e) => setPicked(e.target.value)}
                    data-testid="ws-machine"
                    className="projects-machine-selector"
                    title="Select machine"
                  >
                    {machines.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} {!m.online ? "(Offline)" : ""}
                      </option>
                    ))}
                  </select>
                )}

                {/* Always provide ws-machine for tests even with 1 machine */}
                {machines.length === 1 && (
                  <select
                    value={machineId ?? ""}
                    onChange={(e) => setPicked(e.target.value)}
                    data-testid="ws-machine"
                    style={{ display: "none" }}
                    aria-hidden
                  >
                    <option value={machines[0]!.id}>{machines[0]!.name}</option>
                  </select>
                )}

                <div className="settings-search-bar projects-search-bar">
                  <Search size={14} className="muted" aria-hidden />
                  <input
                    type="text"
                    className="settings-search-input"
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    data-testid="search-projects"
                    aria-label="Search projects"
                  />
                </div>

                <button
                  type="button"
                  className="primary new-project-btn"
                  onClick={() => setNewProjectOpen(true)}
                  disabled={!isMachineOnline}
                  data-testid="new-project-btn"
                >
                  <Plus size={14} aria-hidden /> New Project
                </button>
              </div>
            </div>

            {!isMachineOnline && currentMachine && (
              <div className="msg-warning" style={{ marginTop: 12 }}>
                Machine <strong>{currentMachine.name}</strong> is offline. Start the agent with{" "}
                <code>racler agent start</code> to access its files and projects.
              </div>
            )}
          </section>

          {admin.error && <p className="msg-error">{admin.error}</p>}

      {/* Registered Projects List */}
      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Registered Projects</h3>
            <p className="settings-card-desc">
              Projects registered on {currentMachine?.name ?? "this machine"}.
            </p>
          </div>
        </div>

        <ul className="list" data-testid="project-list">
          {filteredProjects.map((p) => (
            <li
              key={p.id}
              className="list-row static project-row"
              data-testid={`project-row-${p.name}`}
            >
              <span className="row-main">
                <strong>{p.name}</strong>
              </span>
              <span className="muted small" title="Workspace">
                {workspaceName(p.workspaceId)}
              </span>
              <span className="muted small" title="Machine">
                {currentMachine?.name}
              </span>
              <span className="muted small path-cell" title="Local Path">
                {p.location}
              </span>
              <span
                className={`status ${isMachineOnline ? "on" : "off"}`}
                title={isMachineOnline ? "Available: machine is online" : "Machine offline"}
              >
                {isMachineOnline ? "Available" : "Offline"}
              </span>
              <div className="row-actions">
                <button
                  onClick={() => machineId && navigate(projectPath(machineId, p.id))}
                  disabled={!isMachineOnline}
                  data-testid={`open-project-${p.name}`}
                >
                  <FolderOpen size={13} aria-hidden /> Open
                </button>
                <button
                  className="danger-text"
                  onClick={() => setRemoving({ id: p.id, name: p.name })}
                  disabled={!isMachineOnline}
                  data-testid={`remove-project-${p.name}`}
                  title="Remove from racler.dev (files are not deleted)"
                >
                  <Trash2 size={13} aria-hidden /> Remove
                </button>
              </div>
            </li>
          ))}
          {isMachineOnline && admin.projects?.length === 0 && (
            <li className="muted small">No Projects registered yet on this machine.</li>
          )}
          {isMachineOnline &&
            admin.projects &&
            admin.projects.length > 0 &&
            filteredProjects.length === 0 && (
              <li className="muted small">
                No Projects match &ldquo;{projectSearch}&rdquo;.
              </li>
            )}
          {!isMachineOnline && (
            <li className="muted small">
              Projects are unavailable while machine is offline.
            </li>
          )}
        </ul>
      </section>
    </>
  )}

  {/* New Project Modal Dialog */}
  {newProjectOpen && isMachineOnline && (
    <Dialog title="New Project" onClose={() => setNewProjectOpen(false)}>
      <AddProject
        admin={admin}
        initialMode={urlMode ?? "create"}
        machines={machines ?? undefined}
        onSelectMachine={(id) => setPicked(id)}
        onDone={(p) => {
          setNewProjectOpen(false);
          if (machineId) navigate(projectPath(machineId, p.id));
        }}
        onClose={() => setNewProjectOpen(false)}
      />
    </Dialog>
  )}

  {/* Confirm Project Removal Dialog */}
  {removing && machineId && (
    <ConfirmDialog
      title="Remove Project"
      message={
        <>
          Remove <strong>{removing.name}</strong> from racler.dev? Its files are <strong>not</strong>{" "}
          deleted; you can add the folder again later.
        </>
      }
      confirmLabel="Remove"
      danger
      onConfirm={async () => {
        await admin.remove(removing.id);
        setRemoving(null);
      }}
      onClose={() => setRemoving(null)}
    />
  )}
</div>
  );
}
